import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RecurringPayment, CreateRecurringPaymentInput } from '@/types/database/recurring-payments';
import { toast } from 'sonner';

export const useRecurringPayments = () => {
  return useQuery({
    queryKey: ['recurring-payments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recurring_payments' as any)
        .select(`
          *,
          category:expense_categories(id, name, code),
          department:departments(id, name, code)
        `)
        .order('next_due_date', { ascending: true });

      if (error) throw error;
      return data as unknown as RecurringPayment[];
    },
  });
};

export const useCreateRecurringPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRecurringPaymentInput) => {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('recurring_payments' as any)
        .insert({ ...input, created_by: userData.user?.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-payments'] });
      toast.success('Recurring payment created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create recurring payment: ${error.message}`);
    },
  });
};

export const useUpdateRecurringPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<RecurringPayment> & { id: string }) => {
      const { data, error } = await supabase
        .from('recurring_payments' as any)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-payments'] });
      toast.success('Recurring payment updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update recurring payment: ${error.message}`);
    },
  });
};

export const useDeleteRecurringPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('recurring_payments' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-payments'] });
      toast.success('Recurring payment deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete recurring payment: ${error.message}`);
    },
  });
};
